import { useEffect, useRef, useState } from "react";

/**
 * A row of jump links that sticks under the header on a listing page, so a
 * reader on a phone can go straight to the photos, the map or the booking
 * form instead of scrolling a 17,000px page. The current section is marked as
 * the page moves.
 */
export function SectionNav({
  items,
  className = "",
}: {
  items: Array<{ id: string; label: string }>;
  className?: string;
}) {
  const [active, setActive] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Keep the current section's link in view as the row scrolls sideways on a
  // phone; otherwise the marker can sit off the edge where nobody sees it.
  useEffect(() => {
    const list = listRef.current;
    if (!list || !active) return;
    const link = list.querySelector<HTMLElement>(`[data-testid="section-nav-${active}"]`);
    if (!link) return;
    const left = link.offsetLeft - list.clientWidth / 2 + link.clientWidth / 2;
    list.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [active]);

  useEffect(() => {
    const targets = items.map((i) => document.getElementById(i.id)).filter((el): el is HTMLElement => Boolean(el));
    if (targets.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-140px 0px -60% 0px", threshold: 0 },
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [items]);

  const jump = (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", `#${id}`);
  };

  return (
    <nav
      className={`sticky top-[var(--app-header-h,81px)] z-30 border-b border-sand-deep bg-shell/95 backdrop-blur ${className}`}
      aria-label="On this page"
      data-testid="section-nav"
    >
      <div className="container mx-auto max-w-6xl px-4 md:px-6">
        <ul ref={listRef} className="-mx-4 flex gap-1 overflow-x-auto px-4 no-scrollbar md:mx-0 md:px-0">
          {items.map((item) => (
            <li key={item.id} className="shrink-0">
              <a
                href={`#${item.id}`}
                onClick={jump(item.id)}
                className={`inline-flex min-h-[48px] items-center border-b-2 px-3 font-sans text-[12px] uppercase tracking-[0.18em] transition-colors ${
                  active === item.id ? "border-forest text-forest" : "border-transparent text-ink/70 hover:text-ink"
                }`}
                aria-current={active === item.id ? "location" : undefined}
                data-testid={`section-nav-${item.id}`}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
